import React, { useState } from 'react';
import { Box, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, useMediaQuery, useTheme } from '@mui/material';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { useParticipants } from '../hooks/useParticipants';
import { useEventsLogic } from './events/useEventsLogic';
import { useEventBackgrounds } from './events/useEventBackgrounds';
import { Event } from '../types/Event';
import { MEMBER_TYPES } from '../types/User';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getThemeSuffix } from '../utils/eventThemes';
import { useAdminEventStats } from '../hooks/useAdminEventStats';

import EventsHeader from './events/EventsHeader';
import EventsList from './events/EventsList';
import PastEventsList from './events/PastEventsList';
import EventDialogs from './events/EventDialogs';
import EventHeatmap from './events/EventHeatmap';
import EventCalendar from './events/EventCalendar';
import EventDebriefDialog from './events/EventDebriefDialog';
import LocationPopUpDialog from '../components/LocationPopUpDialog';

const Events = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Logic hooks
  const { events, fetchEvents, handleRegisterEvent, handleDeleteEvent, snackbar, setSnackbar } = useEventsLogic();
  const eventBackgrounds = useEventBackgrounds(events);
  const { participants, handleParticipantsClick, participantsDialogOpen, setParticipantsDialogOpen } = useParticipants();

  // Admin stats
  const { stats: adminStats } = useAdminEventStats(events);

  // Local state
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openNonMemberDialog, setOpenNonMemberDialog] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  // Location Dialog State
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [openDebriefDialog, setOpenDebriefDialog] = useState(false);
  const [confirmCreateDialogDate, setConfirmCreateDialogDate] = useState<Date | null>(null);

  // Charger les membres de la famille
  React.useEffect(() => {
    if (user) {
      const q = query(collection(db, 'users'), where('parentId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const members = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFamilyMembers(members);
      });
      return () => unsubscribe();
    } else {
      setFamilyMembers([]);
    }
  }, [user]);

  // Derived state
  const upcomingEvents = React.useMemo(() => events
    .filter(event => new Date(event.endDate) > new Date())
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()), [events]);

  const pastEvents = React.useMemo(() => events
    .filter(event => new Date(event.endDate) <= new Date())
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()), [events]);

  // Handlers
  const onEventClick = React.useCallback((event: Event) => {
    const eventDate = new Date(event.endDate);
    if (eventDate < new Date()) {
      navigate(`/events/${event.id}/debrief?from=events`);
    } else {
      setSelectedEvent(event);
      setOpenRegisterDialog(true);
    }
  }, [navigate]);

  const onEditEvent = React.useCallback((event: Event) => {
    setSelectedEvent(event);
    setOpenCreateDialog(true);
  }, []);

  const onShowParticipants = React.useCallback((event: Event, e: React.MouseEvent) => {
    setSelectedEvent(event);
    handleParticipantsClick(e, event);
  }, [handleParticipantsClick]);

  const onCreateClick = React.useCallback(() => {
    setSelectedEvent(null);
    setOpenCreateDialog(true);
  }, []);

  const handleLocationClick = React.useCallback((locationId: string) => {
    setSelectedLocationId(locationId);
    setIsLocationDialogOpen(true);
  }, []);

  const handleEmptyDayClick = (date: Date) => {
    if (user?.role === MEMBER_TYPES.ADMIN) {
      setConfirmCreateDialogDate(date);
    }
  };

  const handleConfirmCreate = () => {
    if (confirmCreateDialogDate) {
      const startDate = setMinutes(setHours(confirmCreateDialogDate, 10), 0);
      const endDate = setMinutes(setHours(addDays(confirmCreateDialogDate, 1), 2), 0);
      const themeSuffix = getThemeSuffix(startDate);

      // Create a stub event with pre-filled dates
      const newEventStub = {
        startDate: format(startDate, "yyyy-MM-dd'T'HH:mm"),
        endDate: format(endDate, "yyyy-MM-dd'T'HH:mm"),
        title: `LAN ${themeSuffix}`,
        description: '',
        participants: [],
        zone: '', // Default or handled by form
        // Add other required fields if necessary (or rely on form defaults)
      } as unknown as Event; // Casting as Event to satisfy type, knowing id is missing which signifies 'new'

      setSelectedEvent(newEventStub);
      setOpenCreateDialog(true);
      setConfirmCreateDialogDate(null);
    }
  };

  const handleEventClickFromView = (event: Event) => {
    setSelectedEvent(event);
    if (new Date(event.endDate) < new Date()) {
      setOpenDebriefDialog(true);
    } else {
      setOpenRegisterDialog(true);
    }
  };

  return (
    <PageLayout>
      <EventsHeader
        upcomingCount={upcomingEvents.length}
        onCreateClick={onCreateClick}
      />

      {!isMobile && (
        <EventHeatmap
          events={events}
          onEventClick={handleEventClickFromView}
          onEmptyDayClick={handleEmptyDayClick}
          adminStats={adminStats}
        />
      )}

      <EventsList
        events={upcomingEvents}
        onEventClick={onEventClick}
        onEdit={onEditEvent}
        onDelete={handleDeleteEvent}
        onShowParticipants={onShowParticipants}
        familyMembers={familyMembers}
        onLocationClick={handleLocationClick}
      />

      {isMobile && (
        <EventCalendar
          events={events}
          onEventClick={handleEventClickFromView}
          onEmptyDayClick={handleEmptyDayClick}
        />
      )}

      <EventDialogs
        openRegisterDialog={openRegisterDialog}
        setOpenRegisterDialog={setOpenRegisterDialog}
        selectedEvent={selectedEvent}
        user={user}
        onRegisterEvent={(idsToAdd, idsToRemove) => handleRegisterEvent(selectedEvent, setOpenRegisterDialog, idsToAdd, idsToRemove)}

        openParticipantsList={participantsDialogOpen}
        setOpenParticipantsList={setParticipantsDialogOpen}
        participants={participants}

        openCreateDialog={openCreateDialog}
        setOpenCreateDialog={setOpenCreateDialog}
        onEventSaved={fetchEvents}

        openNonMemberDialog={openNonMemberDialog}
        setOpenNonMemberDialog={setOpenNonMemberDialog}
      />

      {/* Confirm Create Dialog */}
      <Dialog
        open={!!confirmCreateDialogDate}
        onClose={() => setConfirmCreateDialogDate(null)}
      >
        <DialogTitle>Créer un événement ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Souhaitez-vous créer un événement pour le <strong>{confirmCreateDialogDate && format(confirmCreateDialogDate, 'dd MMMM yyyy', { locale: fr })}</strong> ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCreateDialogDate(null)}>Annuler</Button>
          <Button onClick={handleConfirmCreate} variant="contained" autoFocus>
            Oui, créer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <LocationPopUpDialog
        open={isLocationDialogOpen}
        onClose={() => setIsLocationDialogOpen(false)}
        locationId={selectedLocationId}
      />

      <EventDebriefDialog
        open={openDebriefDialog}
        onClose={() => setOpenDebriefDialog(false)}
        eventId={selectedEvent?.id || null}
        adminStats={adminStats && selectedEvent?.id ? adminStats[selectedEvent.id] : undefined}
        onEventUpdated={fetchEvents}
        onEdit={onEditEvent}
        onDelete={handleDeleteEvent}
        onLocationClick={handleLocationClick}
      />
    </PageLayout>
  );
};

export default Events;
